import { Form } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useMercuryContext } from "../../context";
import {
  successfulNotification,
  failedNotification,
} from "../../common/utils/notifications";
import CostCodeForm from "./CostCodeRequestForm";

interface CostCodeProps {
  initialValues?: Record<string, any>;
  buttonLabel: string;
  refresh: () => void;
  hasPermissions: boolean;
}

/** The catalogue lives in the invoicing app (`/cost-codes`), since the cost-code rules select from it. */
export const COST_CODES_BASE = "/invoicing-v1/cost-codes";

/**
 * `validFrom` is a date, not an instant: the picker's day is sent as midnight
 * UTC so "1 April" means from 1 April wherever the user sits, and comes back
 * as that same day.
 */
const toValidFrom = (value: any): string | null => (value ? `${dayjs(value).format("YYYY-MM-DD")}T00:00:00.000Z` : null);
const fromValidFrom = (value: any) => (value ? dayjs(String(value).slice(0, 10)) : undefined);

const ManageCostCode: React.FC<CostCodeProps> = ({
  initialValues,
  buttonLabel,
  refresh,
  hasPermissions,
}) => {
  const [form] = Form.useForm();
  const { invoicingClient } = useMercuryContext();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentId, setCurrentId] = useState("");

  const openDrawer = (initialValues?: any) => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (isOpen && initialValues) {
      setCurrentId(initialValues?.id);
      form.setFieldsValue({ ...initialValues, validFrom: fromValidFrom(initialValues?.validFrom) });
    }
  }, [isOpen, initialValues, form]);


  const closeDrawer = () => {
    setIsOpen(false);
    form.resetFields();
  };

  const submitCostCode = async (values: any) => {
    setSubmitting(true);
    // A cleared date on edit is sent as null so it is removed, not kept.
    const payload = { ...values, validFrom: toValidFrom(values.validFrom) };
    try {
       if (!currentId) {
        await invoicingClient.post(COST_CODES_BASE, payload);
      } else {
        await invoicingClient.patch(
          `${COST_CODES_BASE}/${currentId}`,
          payload
        );
      }

      closeDrawer();
      successfulNotification(
        `Cost code is ${currentId ? "updated" : "added"}`,
        "Please refresh your page if the update is not visible."
      );
      refresh();
    } catch (error:any) {
      const body = error?.response?.data;
      const errMsg =
        body?.type === "conflict" || body?.type === "Duplicate_indexed_field"
          ? `Cost code ${body?.details?.code ?? values.code} already exists.`
          : body?.message ||
            error?.message ||
            error?.toString();
      failedNotification(`Failed to ${currentId ? "update" : "add"} cost code`, errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <CostCodeForm
        buttonLabel={buttonLabel}
        form={form}
        isOpen={isOpen}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
        type={currentId ? "Edit" : "Add"}
        onSubmit={(values) => submitCostCode(values)}
        submitting={submitting}
        hasPermissions={hasPermissions}
      />
  );
};

export default ManageCostCode;
